'use client';
import { LinkGroupProps } from './_LinkGroup';
import Image from '@repo/ui/src/components/Image/New';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import { navigationClicked } from '@repo/ui/src/services/analytics';

export interface LinkListProps {
  title?: string;
  columns?: Array<LinkGroupProps>;
  primaryHeader?: string;
  secondaryHeader?: string;
}

const LinkList = ({ columns, title, primaryHeader, secondaryHeader }: LinkListProps) => {
  if (!columns?.length) {
    return null;
  }
  const hasTitles = columns?.some((column) => column.title);
  return (
    <div className="grid gap-y-5 sm:gap-y-6 px-4 sm:px-0">
      {title && <h4 className="tra-text-web-sub-2 text-neutral-700">{title}</h4>}
      <div
        className={classNames(
          'grid grid-cols-2 sm:grid-cols-3 gap-6',
          hasTitles && 'hidden sm:grid',
        )}
        data-testid="link-list-desktop-container"
      >
        {columns?.map((column, i) => (
          <div key={i}>
            {column.title &&
              (column.titleLink ? (
                <h5 className="tra-text-web-sub-2 pb-3">
                  <Anchor
                    link={column.titleLink.link}
                    isExternal={column.titleLink.isExternal}
                    externalLink={column.titleLink.externalLink}
                    noFollow={column.titleLink.noFollow}
                    noIndex={column.titleLink.noIndex}
                    className="block text-neutral-700 hover:underline"
                  >
                    {column.title}
                  </Anchor>
                </h5>
              ) : (
                <h5 className="tra-text-web-sub-2 pb-3">{column.title}</h5>
              ))}
            <ul className="list-none pl-0 grid gap-y-3">
              {column.links?.map(
                (
                  { link, imageContent, displayName, isExternal, noFollow, noIndex, externalLink },
                  i,
                ) => {
                  const imageUrl = imageContent?.image && buildImageSrc(imageContent);
                  return (
                    <li key={i}>
                      <Anchor
                        link={link}
                        externalLink={externalLink}
                        isExternal={isExternal}
                        noFollow={noFollow}
                        noIndex={noIndex}
                        className={classNames(
                          'text-web-body-l text-neutral-700 transition-all duration-100 flex flex-row items-center gap-3 hover:text-brand-400 hover:underline',
                        )}
                        onClick={() =>
                          navigationClicked({
                            nav_type: 'Top Navigation',
                            clicked_text: displayName ?? '',
                            primary_header: primaryHeader ?? '',
                            secondary_header: secondaryHeader ?? '',
                            tertiary_header: `${title ? title + ' ' : ''}${column.title ? column.title + ' ' : ''}${displayName}`,
                          })
                        }
                      >
                        {imageUrl && (
                          <Image
                            src={buildImageSrc(imageContent)}
                            alt={imageContent?.image?.accessibilityLabel}
                            width={24}
                            height={24}
                            intrinsicSize={imageContent?.static}
                            className="w-6"
                          />
                        )}
                        {displayName}
                      </Anchor>
                    </li>
                  );
                },
              )}
            </ul>
          </div>
        ))}
      </div>
      {hasTitles && (
        <Accordion allowZeroExpanded className="sm:hidden">
          {columns.map((column, i) => (
            <AccordionItem
              key={i}
              heading={column.title ?? `${i}`}
              headerFontClass="tra-text-web-sub-2"
              headerClassName="border-b border-warm-200"
              className="!py-2"
            >
              <ul className="list-none pl-0">
                {column.titleLink && (
                  <li>
                    <Anchor
                      link={column.titleLink.link}
                      isExternal={column.titleLink.isExternal}
                      externalLink={column.titleLink.externalLink}
                      noFollow={column.titleLink.noFollow}
                      noIndex={column.titleLink.noIndex}
                      className="tra-text-web-sub-2 block text-neutral-700 py-3 px-4 hover:underline"
                      onClick={() =>
                        navigationClicked({
                          nav_type: 'Top Navigation',
                          clicked_text: column.title ?? '',
                          primary_header: primaryHeader ?? '',
                          secondary_header: secondaryHeader ?? '',
                          tertiary_header: column.title ?? '',
                        })
                      }
                    >
                      {column.title}
                    </Anchor>
                  </li>
                )}
                {column.links?.map(
                  (
                    {
                      link,
                      imageContent,
                      displayName,
                      isExternal,
                      noFollow,
                      noIndex,
                      externalLink,
                    },
                    i,
                  ) => {
                    const imageUrl = imageContent?.image && buildImageSrc(imageContent);
                    return (
                      <li key={i}>
                        <Anchor
                          link={link}
                          externalLink={externalLink}
                          label={displayName}
                          isExternal={isExternal}
                          noFollow={noFollow}
                          noIndex={noIndex}
                          onClick={() =>
                            navigationClicked({
                              nav_type: 'Top Navigation',
                              clicked_text: displayName ?? '',
                              primary_header: primaryHeader ?? '',
                              secondary_header: secondaryHeader ?? '',
                              tertiary_header: `${title ? title + ' ' : ''}${column.title ? column.title + ' ' : ''}${displayName}`,
                            })
                          }
                          className={classNames('text-web-body-l block text-neutral-700 py-3 px-4')}
                        >
                          {imageUrl && (
                            <Image
                              src={buildImageSrc(imageContent)}
                              alt={imageContent?.image?.accessibilityLabel}
                              width={24}
                              height={24}
                              intrinsicSize={imageContent?.static}
                            />
                          )}
                          {displayName}
                        </Anchor>
                      </li>
                    );
                  },
                )}
              </ul>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};

export default LinkList;
